import { useId } from "react";

import { DiagramFrame } from "@/app/features/diagrams/components/shared/DiagramFrame";
import { DiagramMarkers } from "@/app/features/diagrams/components/shared/DiagramMarkers";
import { DiagramNode } from "@/app/features/diagrams/components/shared/DiagramNode";
import {
  type DiagramTopologySpec,
  validateDiagramTopology,
} from "@/app/features/diagrams/functions/diagramTypes";

const offlineTopologySpec: DiagramTopologySpec = {
  nodes: [
    "hermetic-builder",
    "signed-ddn",
    "transfer-dock",
    "active-slot",
    "staging-slot",
    "atomic-switch",
  ],
  junctions: [],
  connectors: [
    {
      id: "build-to-ddn",
      source: { nodeId: "hermetic-builder", anchor: "bottom-center" },
      target: { nodeId: "signed-ddn", anchor: "top-center" },
      meaning: "Build produces signed DDN package",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "ddn-to-transfer",
      source: { nodeId: "signed-ddn", anchor: "bottom-center" },
      target: { nodeId: "transfer-dock", anchor: "top-center" },
      meaning: "Secure dock delivers package to field fleet",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "transfer-to-staging",
      source: { nodeId: "transfer-dock", anchor: "right-center" },
      target: { nodeId: "staging-slot", anchor: "top-center" },
      meaning: "Update written to staging slot B",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "active-to-switch",
      source: { nodeId: "active-slot", anchor: "bottom-center" },
      target: { nodeId: "atomic-switch", anchor: "left-center" },
      meaning: "Current live session served from Slot A",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "staging-to-switch",
      source: { nodeId: "staging-slot", anchor: "bottom-center" },
      target: { nodeId: "atomic-switch", anchor: "right-center" },
      meaning: "Verified delta executed via atomic pointer swap",
      semanticType: "success",
      lineStyle: "solid",
    },
  ],
};

if (process.env.NODE_ENV !== "production") {
  validateDiagramTopology(offlineTopologySpec);
}

export function OfflinePackageUpdateVisual() {
  const titleId = useId();
  const descId = useId();

  return (
    <DiagramFrame
      width={720}
      height={500}
      title="Air-Gapped Offline Delivery & Dual-Slot Update Topology"
      description="Architectural diagram showing deterministic offline packaging with Data Dispatch Notes (DDN), SHA-256 verification, and dual-slot A/B atomic rollout with instant rollback buffer on field maintenance tablets."
      titleId={titleId}
      descId={descId}
      viewBox="0 0 720 500"
      frame={{
        kind: "boundary",
        label: "AIR-GAPPED OFFLINE PACKAGING & FLEET ROLLOUT",
        x: 18,
        y: 18,
        width: 684,
        height: 464,
      }}
    >
      <DiagramMarkers prefix="off-" />

      {/* HERMETIC BUILD PIPELINE */}
      <DiagramNode
        x={190}
        y={40}
        width={340}
        height={48}
        eyebrow="DETERMINISTIC BUILD"
        label="Hermetic Packaging Engine"
        sublabel="Pinned Toolchain · SQLite FTS5 · Vector Assets"
        variant="brass"
      />

      <path
        d="M 360 88 L 360 114"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
        markerEnd="url(#off-arrow-neutral)"
      />

      {/* SIGNED DDN PACKAGE */}
      <DiagramNode
        x={170}
        y={116}
        width={380}
        height={50}
        eyebrow="STANDARDIZED EXCHANGE"
        label="Signed Data Dispatch Note (DDN)"
        sublabel="SHA-256 Manifest · Radio Version: FLEET-V4.2-R18"
        variant="standard"
      />

      <path
        d="M 360 166 L 360 196"
        fill="none"
        stroke="var(--ga-diagram-agent, #2563eb)"
        strokeWidth="1.5"
        strokeDasharray="4 3"
        markerEnd="url(#off-arrow-blue)"
      />

      {/* AIR-GAP TRANSFER DOCK */}
      <DiagramNode
        x={200}
        y={198}
        width={320}
        height={46}
        label="Field Maintenance Gateway"
        sublabel="Signature Verification & Fleet Synchronization"
        variant="standard"
      />

      {/* CONNECTORS TO SLOTS */}
      <path
        d="M 280 244 L 195 244 L 195 288"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.5"
        markerEnd="url(#off-arrow-neutral)"
      />
      <path
        d="M 440 244 L 525 244 L 525 288"
        fill="none"
        stroke="var(--ga-diagram-success, #15803d)"
        strokeWidth="1.5"
        markerEnd="url(#off-arrow-green)"
      />

      {/* FIELD TABLET HARDWARE FRAME */}
      <rect
        x="45"
        y="266"
        width="630"
        height="198"
        rx="8"
        fill="none"
        stroke="var(--ga-line, rgba(28,26,23,0.15))"
        strokeWidth="1"
        strokeDasharray="3 3"
      />
      <text
        x="60"
        y="282"
        fill="currentColor"
        className="text-[10px] font-mono tracking-wider opacity-60 font-semibold uppercase"
      >
        Flight-Line Tablet Local Runtime (Dual-Slot Architecture)
      </text>

      {/* ACTIVE SLOT A */}
      <DiagramNode
        x={70}
        y={292}
        width={250}
        height={66}
        eyebrow="SLOT A (LIVE)"
        label="Active Publication: R17"
        sublabel="Serving Field Technician Without Network"
        variant="standard"
      />

      {/* STAGING SLOT B */}
      <DiagramNode
        x={400}
        y={292}
        width={250}
        height={66}
        eyebrow="SLOT B (STAGING)"
        label="Staged Package: R18"
        sublabel="Verified Incremental Delta Payload"
        variant="repair"
      />

      {/* ATOMIC SYMLINK SWITCH */}
      <path
        d="M 195 358 L 195 378 L 320 378 L 320 393"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.5"
        markerEnd="url(#off-arrow-neutral)"
      />
      <path
        d="M 525 358 L 525 378 L 400 378 L 400 393"
        fill="none"
        stroke="var(--ga-diagram-success, #15803d)"
        strokeWidth="1.8"
        markerEnd="url(#off-arrow-green)"
      />

      <DiagramNode
        x={220}
        y={395}
        width={280}
        height={54}
        eyebrow="ZERO-DOWNTIME SWAP"
        label="Atomic Symlink Pointer Switch"
        sublabel="Instant Activation · Instant Rollback Buffer"
        variant="success"
      />
    </DiagramFrame>
  );
}
